import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useInvalidatingMutation } from "@/lib/mutations";
import { isTransitionalStatus, LIST_POLL_MS } from "@/lib/polling";

import {
  closeBreakGlass,
  createApp,
  createConnector,
  createConnectorGroup,
  createGroup,
  createIdentityProvider,
  createOrg,
  createPolicy,
  createUser,
  deleteApp,
  deleteConnector,
  deleteConnectorGroup,
  deleteGroup,
  deleteIdentityProvider,
  deleteOrg,
  deletePolicy,
  deleteUser,
  drainConnector,
  getApp,
  getOrg,
  getPolicy,
  getSignIn,
  listApps,
  listAppDecisions,
  listUserDecisions,
  listConnectorGroups,
  listConnectors,
  listDevices,
  listGroups,
  listIdentityProviders,
  listPolicies,
  listRequests,
  listSessions,
  listUsers,
  newInstallBlock,
  openBreakGlass,
  readDirectory,
  readProviderTest,
  resumeConnector,
  setAppPolicies,
  startProviderTest,
  syncConnectorApps,
  testPolicies,
  updateApp,
  updateConnectorGroup,
  updateIdentityProvider,
  updateOrg,
  updatePolicy,
  updateUser,
} from "./api";
import type {
  AccessAppRequest,
  AccessGroupRequest,
  AccessPolicyRequest,
  AccessUserCreateRequest,
  AccessUserUpdateRequest,
  BreakGlassRequest,
  PolicyTestRequest,
  ConnectorCreateRequest,
  ConnectorGroupRequest,
  IdentityProviderRequest,
  Org,
  OrgCreateRequest,
  OrgUpdateRequest,
} from "./types";

export const accessKeys = {
  all: ["access"] as const,
  org: () => ["access", "org"] as const,
  groups: () => ["access", "connector-groups"] as const,
  connectors: () => ["access", "connectors"] as const,
  apps: () => ["access", "apps"] as const,
  app: (id: string) => ["access", "apps", id] as const,
  policies: () => ["access", "policies"] as const,
  policy: (id: string) => ["access", "policies", id] as const,
  users: () => ["access", "users"] as const,
  devices: () => ["access", "devices"] as const,
  sessions: () => ["access", "sessions"] as const,
  requests: () => ["access", "requests"] as const,
  signIn: () => ["access", "sign-in"] as const,
  decisions: (filter: { app_id?: string; user_id?: string }) =>
    ["access", "decisions", filter.app_id ?? "", filter.user_id ?? ""] as const,
  allDecisions: () => ["access", "decisions"] as const,
  userGroups: () => ["access", "groups"] as const,
  providers: () => ["access", "identity-providers"] as const,
  providerTest: (id: string) => ["access", "identity-providers", id, "test"] as const,
};

/** The project's organisation, or `null` when it has none yet (`getOrg`
 *  asks for a 204 then; a 404 from an older backend is the same state). */
export function useOrg(enabled = true) {
  return useQuery<Org | null>({
    queryKey: accessKeys.org(),
    queryFn: async () => {
      try {
        return await getOrg();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
      }
    },
    enabled,
    retry: false,
    refetchInterval: (query) =>
      isTransitionalStatus(query.state.data?.status) ? LIST_POLL_MS : false,
  });
}

export function useConnectorGroups(enabled = true) {
  return useQuery({ queryKey: accessKeys.groups(), queryFn: () => listConnectorGroups(), enabled });
}

/** Polls while a connector is joining, so it turns healthy on the same page;
 *  slower otherwise (a connector reports every 30 s). */
export function useConnectors(enabled = true) {
  return useQuery({
    queryKey: accessKeys.connectors(),
    queryFn: () => listConnectors(),
    enabled,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((c) => c.status === "enrolling") ? LIST_POLL_MS : 30_000,
  });
}

export function useApps(enabled = true) {
  return useQuery({
    queryKey: accessKeys.apps(),
    queryFn: () => listApps(),
    enabled,
    // an open break-glass ends on the clock: follow it
    refetchInterval: (query) =>
      (query.state.data ?? []).some((a) => a.break_glass?.length) ? 30_000 : false,
  });
}

export function useApp(appId: string | undefined) {
  return useQuery({
    queryKey: accessKeys.app(appId ?? ""),
    queryFn: () => getApp(appId as string),
    enabled: Boolean(appId),
  });
}

export function usePolicies(enabled = true) {
  return useQuery({ queryKey: accessKeys.policies(), queryFn: () => listPolicies(), enabled });
}

export function usePolicy(policyId: string | undefined) {
  return useQuery({
    queryKey: accessKeys.policy(policyId ?? ""),
    queryFn: () => getPolicy(policyId as string),
    enabled: Boolean(policyId),
  });
}

export function useAccessUsers(enabled = true) {
  return useQuery({ queryKey: accessKeys.users(), queryFn: () => listUsers(), enabled });
}

export function useAccessDevices(enabled = true) {
  return useQuery({ queryKey: accessKeys.devices(), queryFn: () => listDevices(), enabled });
}

export function useAccessSessions(enabled = true) {
  return useQuery({ queryKey: accessKeys.sessions(), queryFn: () => listSessions(), enabled });
}

export function useAccessRequests(enabled = true) {
  return useQuery({ queryKey: accessKeys.requests(), queryFn: () => listRequests(), enabled });
}

// -- changes -----------------------------------------------------------------
export function useCreateOrg() {
  return useInvalidatingMutation((payload: OrgCreateRequest) => createOrg(payload), {
    invalidates: [accessKeys.all],
    successMessage: "Organisation created",
    errorMessage: "Could not create the organisation",
  });
}

export function useUpdateOrg() {
  return useInvalidatingMutation((payload: OrgUpdateRequest) => updateOrg(payload), {
    invalidates: [accessKeys.org()],
    successMessage: "Saved",
    errorMessage: "Could not save the organisation",
  });
}

export function useDeleteOrg() {
  return useInvalidatingMutation(() => deleteOrg(), {
    invalidates: [accessKeys.all],
    successMessage: "Organisation removed",
    errorMessage: "Could not remove the organisation",
  });
}

export function useCreateConnectorGroup() {
  return useInvalidatingMutation(
    (payload: ConnectorGroupRequest) => createConnectorGroup(payload),
    {
      invalidates: [accessKeys.groups(), accessKeys.org()],
      successMessage: "Connector group created",
      errorMessage: "Could not create the connector group",
    },
  );
}

export function useUpdateConnectorGroup() {
  return useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: ConnectorGroupRequest }) =>
      updateConnectorGroup(id, payload),
    {
      invalidates: [accessKeys.groups()],
      successMessage: "Saved",
      errorMessage: "Could not save the connector group",
    },
  );
}

export function useDeleteConnectorGroup() {
  return useInvalidatingMutation((id: string) => deleteConnectorGroup(id), {
    invalidates: [accessKeys.groups(), accessKeys.org()],
    successMessage: "Connector group removed",
    errorMessage: "Could not remove the connector group",
  });
}

const CONNECTOR_KEYS = [accessKeys.connectors(), accessKeys.groups(), accessKeys.org()];

export function useCreateConnector() {
  return useInvalidatingMutation((payload: ConnectorCreateRequest) => createConnector(payload), {
    invalidates: CONNECTOR_KEYS,
    errorMessage: "Could not add the connector",
  });
}

export function useNewInstallBlock() {
  return useInvalidatingMutation((id: string) => newInstallBlock(id), {
    invalidates: [accessKeys.connectors()],
    errorMessage: "Could not make a new install block",
  });
}

export function useDrainConnector() {
  return useInvalidatingMutation(
    ({ id, drain }: { id: string; drain: boolean }) =>
      drain ? drainConnector(id) : resumeConnector(id),
    {
      invalidates: CONNECTOR_KEYS,
      successMessage: (_, { drain }) =>
        drain ? "Drained: new sessions go through the group's other connectors" : "Back in service",
      errorMessage: "Could not change the connector",
    },
  );
}

export function useDeleteConnector() {
  return useInvalidatingMutation((id: string) => deleteConnector(id), {
    invalidates: CONNECTOR_KEYS,
    successMessage: "Connector removed",
    errorMessage: "Could not remove the connector",
  });
}

export function useSyncConnectorApps() {
  return useInvalidatingMutation(() => syncConnectorApps(), {
    invalidates: [accessKeys.connectors()],
    errorMessage: "Could not tell the network about your apps",
  });
}

export function useCreateApp() {
  return useInvalidatingMutation((payload: AccessAppRequest) => createApp(payload), {
    invalidates: [accessKeys.apps(), accessKeys.org()],
    successMessage: "App added",
    errorMessage: "Could not add the app",
  });
}

export function useUpdateApp() {
  return useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: AccessAppRequest }) => updateApp(id, payload),
    {
      invalidates: [accessKeys.apps()],
      successMessage: "Saved",
      errorMessage: "Could not save the app",
    },
  );
}

export function useDeleteApp() {
  return useInvalidatingMutation((id: string) => deleteApp(id), {
    invalidates: [accessKeys.apps(), accessKeys.org(), accessKeys.policies()],
    successMessage: "App removed",
    errorMessage: "Could not remove the app",
  });
}

export function useSetAppPolicies() {
  return useInvalidatingMutation(
    ({ id, policyIds }: { id: string; policyIds: string[] }) => setAppPolicies(id, policyIds),
    {
      invalidates: [accessKeys.apps(), accessKeys.policies(), accessKeys.allDecisions()],
      successMessage: "Access policies saved",
      errorMessage: "Could not save the access policies",
    },
  );
}

export function useCreatePolicy() {
  return useInvalidatingMutation((payload: AccessPolicyRequest) => createPolicy(payload), {
    invalidates: [accessKeys.policies(), accessKeys.org()],
    successMessage: "Access policy created",
    errorMessage: "Could not create the access policy",
  });
}

export function useUpdatePolicy() {
  return useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: AccessPolicyRequest }) => updatePolicy(id, payload),
    {
      invalidates: [accessKeys.policies(), accessKeys.apps(), accessKeys.allDecisions()],
      successMessage: "Saved",
      errorMessage: "Could not save the access policy",
    },
  );
}

export function useDeletePolicy() {
  return useInvalidatingMutation((id: string) => deletePolicy(id), {
    invalidates: [
      accessKeys.policies(),
      accessKeys.org(),
      accessKeys.apps(),
      accessKeys.allDecisions(),
    ],
    successMessage: "Access policy removed",
    errorMessage: "Could not remove the access policy",
  });
}

// -- identity (PA-4) ---------------------------------------------------------
export function useSignIn(enabled = true) {
  return useQuery({ queryKey: accessKeys.signIn(), queryFn: () => getSignIn(), enabled });
}

export function useAccessGroups(enabled = true) {
  return useQuery({ queryKey: accessKeys.userGroups(), queryFn: () => listGroups(), enabled });
}

export function useIdentityProviders(enabled = true) {
  return useQuery({
    queryKey: accessKeys.providers(),
    queryFn: () => listIdentityProviders(),
    enabled,
  });
}

/** The outcome of a sign-in that is being tried: asked every few seconds
 *  while it is awaited, then left alone. */
export function useProviderTest(providerId: string | undefined, watching: boolean) {
  return useQuery({
    queryKey: accessKeys.providerTest(providerId ?? ""),
    queryFn: () => readProviderTest(providerId as string),
    enabled: Boolean(providerId) && watching,
    retry: false,
    refetchInterval: (query) => (query.state.data?.result === "waiting" ? 3000 : false),
  });
}

const DIRECTORY = [accessKeys.users(), accessKeys.userGroups(), accessKeys.org()];

export function useCreateUser() {
  return useInvalidatingMutation((payload: AccessUserCreateRequest) => createUser(payload), {
    invalidates: DIRECTORY,
    successMessage: (user) => user.warning ?? "User added",
    errorMessage: "Could not add the user",
  });
}

export function useUpdateUser() {
  return useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: AccessUserUpdateRequest }) =>
      updateUser(id, payload),
    {
      invalidates: DIRECTORY,
      successMessage: (user) => user.warning ?? "Saved",
      errorMessage: "Could not save the user",
    },
  );
}

export function useDeleteUser() {
  return useInvalidatingMutation((id: string) => deleteUser(id), {
    invalidates: DIRECTORY,
    successMessage: "User removed",
    errorMessage: "Could not remove the user",
  });
}

export function useReadDirectory() {
  return useInvalidatingMutation(() => readDirectory(), {
    invalidates: [...DIRECTORY, accessKeys.signIn()],
    successMessage: (read) =>
      read.added || read.removed
        ? `${read.added} added, ${read.removed} removed`
        : "Nothing has changed",
    errorMessage: "Could not read the directory",
  });
}

export function useCreateGroup() {
  return useInvalidatingMutation((payload: AccessGroupRequest) => createGroup(payload), {
    invalidates: [accessKeys.userGroups()],
    successMessage: "Group created",
    errorMessage: "Could not create the group",
  });
}

export function useDeleteGroup() {
  return useInvalidatingMutation((id: string) => deleteGroup(id), {
    invalidates: DIRECTORY,
    successMessage: "Group removed",
    errorMessage: "Could not remove the group",
  });
}

const PROVIDERS = [accessKeys.providers(), accessKeys.signIn(), accessKeys.userGroups()];

export function useCreateIdentityProvider() {
  return useInvalidatingMutation(
    (payload: IdentityProviderRequest) => createIdentityProvider(payload),
    {
      invalidates: PROVIDERS,
      successMessage: "Identity provider added",
      errorMessage: "Could not add the identity provider",
    },
  );
}

export function useUpdateIdentityProvider() {
  return useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: IdentityProviderRequest }) =>
      updateIdentityProvider(id, payload),
    {
      invalidates: PROVIDERS,
      successMessage: "Saved",
      errorMessage: "Could not save the identity provider",
    },
  );
}

export function useDeleteIdentityProvider() {
  return useInvalidatingMutation((id: string) => deleteIdentityProvider(id), {
    invalidates: PROVIDERS,
    successMessage: "Identity provider removed",
    errorMessage: "Could not remove the identity provider",
  });
}

export function useStartProviderTest() {
  return useInvalidatingMutation((id: string) => startProviderTest(id), {
    invalidates: [accessKeys.providers()],
    errorMessage: "Could not start the test",
  });
}

// -- decisions, break-glass and the tester (PA-8, PA-9) -----------------------
/** The evaluator recomputes every 15 s (ops doc §12.1): the console follows. */
const DECISIONS_POLL_MS = 15_000;

/** Decisions for an app, a user, or one user on one app. */
export function useDecisions(filter: { app_id?: string; user_id?: string }, enabled = true) {
  const { app_id, user_id } = filter;
  return useQuery({
    queryKey: accessKeys.decisions(filter),
    queryFn: async () => {
      if (app_id) {
        const rows = await listAppDecisions(app_id);
        return user_id ? rows.filter((d) => d.user_id === user_id) : rows;
      }
      return listUserDecisions(user_id!);
    },
    enabled: enabled && Boolean(app_id || user_id),
    refetchInterval: DECISIONS_POLL_MS,
  });
}

const BREAK_GLASS_KEYS = [accessKeys.apps(), accessKeys.allDecisions()];

export function useOpenBreakGlass() {
  return useInvalidatingMutation(
    ({ appId, payload }: { appId: string; payload: BreakGlassRequest }) =>
      openBreakGlass(appId, payload),
    {
      invalidates: BREAK_GLASS_KEYS,
      successMessage: "Break-glass access open",
      errorMessage: "Could not open the break-glass access",
    },
  );
}

export function useCloseBreakGlass() {
  return useInvalidatingMutation(
    ({ appId, user }: { appId: string; user: string }) => closeBreakGlass(appId, user),
    {
      invalidates: BREAK_GLASS_KEYS,
      successMessage: "Break-glass access closed",
      errorMessage: "Could not close the break-glass access",
    },
  );
}

export function useTestPolicies() {
  return useMutation({ mutationFn: (payload: PolicyTestRequest) => testPolicies(payload) });
}
